import { type FC, useCallback, useMemo } from "react";

import { Modal, Spinner } from "@snapchat/snap-design-system";

import { type Discoverability } from "../../gql-generated/types";
import { Flexbox } from "../../shared/components";
import { type ModalProps } from "../../shared/hooks";
import { LensDiscoverabilityFormSchema, type LensDiscoverabilityFormValues } from "../models";

import { LensDiscoverabilityForm, LensDiscoverabilityFormik } from "./LensDiscoverabilityForm";
import { ModalDescription, ModalTitle, containerStyles, formStyles } from "./LensDiscoverabilityModal.styles";
import {
    LensDiscoverabilityLabel,
    LensDiscoverabilityModalActionText,
    LensDiscoverabilityModalCancelText,
    LensDiscoverabilityModalDescription,
} from "./messages.generated";

interface LensDiscoverabilityModal extends ModalProps {
    lensOrSubmissionId: string;
    discoverability: Discoverability | null;
    onSave: (discoverability: Discoverability) => void;
    saveLoading: boolean;
}

// TODO(scao): WIP
export const LensDiscoverabilityModal: FC<LensDiscoverabilityModal> = ({
    lensOrSubmissionId,
    discoverability,
    visible,
    onSave,
    onCancel,
    saveLoading,
}) => {
    const initialValues: LensDiscoverabilityFormValues = useMemo(
        () =>
            discoverability
                ? {
                      description: discoverability.description ?? "",
                      tagsList: [...(discoverability.tagsList ?? [])],
                      locationsList: [...(discoverability.locationsList ?? [])],
                      daysTimesHolidaysList: [...(discoverability.daysTimesHolidaysList ?? [])],
                  }
                : {
                      description: "",
                      tagsList: [],
                      locationsList: [],
                      daysTimesHolidaysList: [],
                  },
        [discoverability],
    );

    const handleFormikSubmit = useCallback(
        (values: LensDiscoverabilityFormValues) => {
            const { description, tagsList, locationsList, daysTimesHolidaysList } =
                LensDiscoverabilityFormSchema.cast(values);
            onSave({
                description: description,
                tagsList: [...(tagsList ?? [])],
                locationsList: [...(locationsList ?? [])],
                daysTimesHolidaysList: [...(daysTimesHolidaysList ?? [])],
            });
        },
        [onSave],
    );
    return (
        <LensDiscoverabilityFormik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleFormikSubmit}
            validationSchema={LensDiscoverabilityFormSchema}
            validateOnMount={true}
        >
            {({ submitForm, isValid, resetForm }) => (
                <Modal
                    visible={visible}
                    title={
                        <ModalTitle>
                            <LensDiscoverabilityLabel />
                        </ModalTitle>
                    }
                    onAction={submitForm}
                    actionText={saveLoading ? <Spinner /> : <LensDiscoverabilityModalActionText />}
                    isActionButtonDisabled={!isValid}
                    isActionButtonPending={false}
                    onCancel={() => {
                        resetForm();
                        onCancel();
                    }}
                    cancelText={<LensDiscoverabilityModalCancelText />}
                    isCancelButtonDisabled={false}
                    maskClosable={true}
                    width={900}
                    padBody={false}
                >
                    <Flexbox className={containerStyles} flexDirection="column">
                        <ModalDescription>
                            <LensDiscoverabilityModalDescription />
                        </ModalDescription>
                        <Flexbox className={formStyles} flexDirection="column">
                            <LensDiscoverabilityForm lensOrSubmissionId={lensOrSubmissionId} />
                        </Flexbox>
                    </Flexbox>
                </Modal>
            )}
        </LensDiscoverabilityFormik>
    );
};
